import type { BadgeVariant } from '@/components/ui/badge';
import { DateTime } from 'luxon';

/**
 * The invoice status state machine, extracted from `invoice-status.tsx` so
 * the datawarehouse Avier & Fakturor report can reuse it on DW documents
 * (snake_case, no matrix metas). The branch ORDER is the contract — it
 * mirrors what users see in the invoicing list. Change the two files
 * together: this core and the `getInvoiceStatus` mapper are one machine.
 */
export interface InvoiceStatusInput {
  /** Legacy: invoiceValuesMeta.total_value ?? 0. DW: Σ rows net+vat. */
  totalValue: number;
  closedTime: string | null | undefined;
  /** debt_invoice_counts.unclosed_debt_invoices */
  unclosedDebtInvoices: number | null | undefined;
  /** debt_invoice_counts.kinds['1'] — debt collection invoices sent. */
  sentDebtCount: number | null | undefined;
  /** debt_invoice_counts.kinds['0'] — reminders sent. */
  sentReminderCount: number | null | undefined;
  automaticDebtInvoicingPaused: boolean | null | undefined;
  attestedDate: string | null | undefined;
  distributionDate: string | null | undefined;
  dueDate: string | null | undefined;
  /** Legacy: invoiceComputedMeta.payments_sum. DW: invoice.paid_amount. */
  paymentsSum: number | null | undefined;
  invoiceNumber: number | string | null | undefined;
  ocrNumber: string | null | undefined;
}

export interface InvoiceStatusResult {
  text: string;
  badgeVariant: BadgeVariant['variant'];
  additionalInfo?: string;
}

/**
 * One day's grace after the due date before an invoice counts as overdue.
 * Shared with the invoice timeline so the rail and the badge flip together.
 */
export function isPastDue(
  dueDate: string | null | undefined,
  now: DateTime = DateTime.now()
) {
  return dueDate ? now > DateTime.fromISO(dueDate).plus({ days: 1 }) : false;
}

function getCreditStatus(input: InvoiceStatusInput): InvoiceStatusResult {
  if (!input.attestedDate) {
    return { text: 'Kredit, ej attesterad', badgeVariant: 'yellow' };
  }
  if (input.closedTime) {
    return { text: 'Kredit, nyttjad', badgeVariant: 'outline' };
  }
  if (input.paymentsSum && input.paymentsSum < 0) {
    return { text: 'Kredit, delvis nyttjad', badgeVariant: 'cyan' };
  }
  return { text: 'Kredit, onyttjad', badgeVariant: 'orange' };
}

export function getInvoiceStatusCore(
  input: InvoiceStatusInput,
  now: DateTime = DateTime.now()
): InvoiceStatusResult {
  if (input.totalValue < 0) {
    return getCreditStatus(input);
  }

  if (input.closedTime) {
    if (input.unclosedDebtInvoices && input.unclosedDebtInvoices > 0) {
      return {
        text: 'Slutbetald (Öppna påminnelser/krav)',
        badgeVariant: 'orange',
      };
    }
    return { text: 'Slutbetald', badgeVariant: 'green' };
  }

  const isPaused = !!input.automaticDebtInvoicingPaused;
  const paused = isPaused ? ' (Pausad)' : '';
  const overdueVariant: BadgeVariant['variant'] = isPaused
    ? 'light-gray'
    : 'destructive';

  // A reminder or krav can only be created on an attested invoice past its
  // due date, and the due date is locked after attest — so a sent one always
  // means the invoice is overdue, and the prefix cannot be wrong.
  if (input.sentDebtCount !== undefined && (input.sentDebtCount ?? 0) > 0) {
    return {
      text: `Förfallen, kravhantering${paused}`,
      badgeVariant: overdueVariant,
    };
  }

  if (
    input.sentReminderCount !== undefined &&
    (input.sentReminderCount ?? 0) > 0
  ) {
    return {
      text: `Förfallen, påminnelse skickad${paused}`,
      badgeVariant: overdueVariant,
    };
  }

  const pastDuePlusOne = isPastDue(input.dueDate, now);

  if (pastDuePlusOne && !input.closedTime && input.attestedDate) {
    return { text: `Förfallen${paused}`, badgeVariant: overdueVariant };
  }

  if (input.distributionDate && input.attestedDate && !input.closedTime) {
    if (input.paymentsSum && input.paymentsSum > 0) {
      return {
        text: `Skickad, delbetald${paused}`,
        badgeVariant: isPaused ? 'light-gray' : 'cyan',
      };
    }
    return {
      text: `Skickad${paused}`,
      badgeVariant: isPaused ? 'light-gray' : 'blue',
    };
  }

  if (!input.attestedDate) {
    return { text: 'Ej attesterad', badgeVariant: 'yellow' };
  }

  if (input.invoiceNumber && input.ocrNumber) {
    return { text: 'Attesterad', badgeVariant: 'green' };
  }

  return {
    text: 'Misslyckad attestering',
    badgeVariant: 'destructive',
    additionalInfo:
      'Attesteringen av denna avi/faktura misslyckades. Försök igen.',
  };
}
